import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { BrowserRouter } from 'react-router-dom';
import localforage from 'localforage';
import App from './App';
import './index.css';
import { useAuthStore } from './stores/authStore';
import { initializeErrorTracking } from './services/error-tracking';
import { initSentryWeb } from './services/sentry';
import { installChunkReloadHandler } from './lib/chunkReload';
import { shouldRetryMutation } from './lib/mutationRetry';

// Expose auth store for E2E testing (development and test builds only)
if (!import.meta.env.PROD) {
  (window as unknown as { __testAuth: typeof useAuthStore }).__testAuth = useAuthStore;
}

// Auto-recover (once) from stale lazy-route chunks after a deploy
installChunkReloadHandler();

// Initialize error tracking (captures window.onerror, unhandled rejections, breadcrumbs)
initializeErrorTracking();

// Initialize Sentry (no-op until VITE_SENTRY_DSN is set in Vercel env).
// Runs alongside the existing /api/v1/errors/log pipeline so we get both
// the in-house dashboard and Sentry's stack-trace + release tracking.
initSentryWeb();

// Create persister with IndexedDB (via localforage) for offline cache
const persister = createAsyncStoragePersister({
  storage: localforage,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // Fresh for 30 seconds
      gcTime: 10 * 60 * 1000, // Cache for 10 minutes
      retry: 3, // Retry up to 3 times
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Exponential backoff: 1s, 2s, 4s (max 30s)
      refetchOnWindowFocus: false,
    },
    mutations: {
      // Retry only a genuine network failure (fetch TypeError) up to 3 times; never a server answer or ApiRequestError (lib/mutationRetry.ts)
      retry: shouldRetryMutation,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 60 * 24, // Persist for 24 hours
        buster: 'v1', // Increment to invalidate old cache
      }}
    >
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </PersistQueryClientProvider>
  </React.StrictMode>
);

// Register service worker for PWA functionality
if ('serviceWorker' in navigator) {
  // Use dynamic import to handle vite-plugin-pwa virtual module
  import('virtual:pwa-register').then(({ registerSW }) => {
    const updateSW = registerSW({
      onNeedRefresh() {
        // Show update notification
        if (confirm('New version available! Reload to update?')) {
          updateSW(true);
        }
      },
      onOfflineReady() {
        console.log('App ready to work offline');
      },
      onRegistered(registration: ServiceWorkerRegistration | undefined) {
        console.log('Service Worker registered:', registration);
      },
      onRegisterError(error: unknown) {
        console.error('Service Worker registration error:', error);
      },
    });
  }).catch((error) => {
    console.log('PWA not available in development mode:', error);
  });
}
